import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { api } from "../api/axios";
import { Pill } from "../components/ui";

const STATUS_TONE = { pass: "success", fail: "error", na: "neutral", unknown: "warning" };

function statusLabel(s) {
  if (s === "pass") return "עובר";
  if (s === "fail") return "נכשל";
  if (s === "na") return "לא רלוונטי";
  return "לא ידוע";
}

function severityLabel(s) {
  if (s === "high") return "גבוהה";
  if (s === "medium") return "בינונית";
  if (s === "low") return "נמוכה";
  return s || "—";
}

export default function ComplianceDiffReport() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const BackIcon = isRtl ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const from = sp.get("from");
    const to = sp.get("to");
    const qs = from && to ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : "";
    const { data } = await api.get(`/api/compliance/sites/${id}/history-diff${qs}`);
    setData(data);
  }, [id, sp]);

  useEffect(() => {
    load().catch((err) => setError(err?.response?.data?.message || "שגיאה"));
  }, [load]);

  const print = () => window.print();

  const createdStr = useMemo(() => new Date().toLocaleString("he-IL"), []);
  const fromStr = useMemo(() => (data?.from?.created_at ? new Date(data.from.created_at).toLocaleString("he-IL") : ""), [data?.from]);
  const toStr = useMemo(() => (data?.to?.created_at ? new Date(data.to.created_at).toLocaleString("he-IL") : ""), [data?.to]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Skeleton variant="rounded" height={400} sx={{ maxWidth: 1000, mx: "auto" }} />;

  const scoreDelta = Number(data.scoreDelta || 0);
  const deltaLabel = scoreDelta === 0 ? "ללא שינוי" : scoreDelta > 0 ? `+${scoreDelta}` : `${scoreDelta}`;

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
    <Button className="aw-print-hide" component={RouterLink} to={`/sites/${id}/compliance`} size="small" startIcon={<BackIcon />} sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}>
      מרכז תאימות
    </Button>
    <Card sx={{ "@media print": { border: 0, boxShadow: "none" } }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
        <Box
          component="style"
          dangerouslySetInnerHTML={{
            __html: `
              @media print {
                .aw-print-hide { display: none !important; }
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                @page { margin: 16mm; }
              }
            `,
          }}
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            דוח שינויי תאימות (Snapshot Diff)
          </Typography>
          <Button className="aw-print-hide" variant="contained" onClick={print} startIcon={<PrintOutlinedIcon />}>
            הדפסה / שמירה כ‑PDF
          </Button>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1.5 }}>
          נוצר: <b>{createdStr}</b>
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          אתר: <b>{data.site?.name}</b> · {data.site?.domain}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
          השוואה: <b>{fromStr}</b> {isRtl ? "←" : "→"} <b>{toStr}</b>
        </Typography>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
          <Pill tone={scoreDelta > 0 ? "success" : scoreDelta < 0 ? "error" : "neutral"}>Δ ציון: {deltaLabel}</Pill>
          <Pill tone="success">שיפורים: {data.buckets?.improvement || 0}</Pill>
          <Pill tone="error">החמרות: {data.buckets?.regression || 0}</Pill>
          <Pill tone="info">נסגרו: {data.buckets?.resolved || 0}</Pill>
          <Pill tone="warning">התדרדרו ללא ידוע: {data.buckets?.lost || 0}</Pill>
          <Pill tone="neutral">שינויים אחרים: {data.buckets?.changed || 0}</Pill>
        </Stack>

        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>בדיקה</TableCell>
              <TableCell>קטגוריה</TableCell>
              <TableCell>תקן</TableCell>
              <TableCell>חומרה</TableCell>
              <TableCell>מ־</TableCell>
              <TableCell>ל־</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.changes || []).map((c) => (
              <TableRow key={c.check_id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {c.title}
                  </Typography>
                  {c.to?.notes || c.from?.notes ? (
                    <Typography variant="caption" component="div">
                      הערות: {c.from?.notes ? `(${c.from.notes})` : ""} {c.to?.notes ? `→ (${c.to.notes})` : ""}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{c.category || "—"}</TableCell>
                <TableCell>{c.standard_ref || "—"}</TableCell>
                <TableCell>{severityLabel(c.severity)}</TableCell>
                <TableCell><Pill tone={STATUS_TONE[c.from?.status] || "warning"}>{statusLabel(c.from?.status)}</Pill></TableCell>
                <TableCell><Pill tone={STATUS_TONE[c.to?.status] || "warning"}>{statusLabel(c.to?.status)}</Pill></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>

        <Box sx={{ color: "text.secondary" }}>
          <Typography variant="caption">
            דוח זה נוצר מתוך Accss ומציג שינויים בין שני Snapshots. מומלץ לבצע בדיקות ידניות נוספות לפי הצורך.
          </Typography>
        </Box>
      </CardContent>
    </Card>
    </Box>
  );
}


